import {
  CustomMappingType,
  IFailedCustomMappingRecord,
  ISourceEntityTypes,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  PricedServiceCodeTranslator,
  ServiceProviderHandler,
  TreatmentConfiguration,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  ITreatmentConfiguration,
  ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  XSLXImporterExporter,
  multiSortBy$,
  nameSorter,
  resolveWithConcurrencyForAll,
  snapshot,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sortBy } from 'lodash';
import {
  BaseCustomMappingHandler,
  DELETE_CONCURRENCY,
} from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { IBaseMigrationItemCode } from '../interfaces';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { ITEM_CODE_TO_TREATMENT_DEFAULT_MAPPINGS } from './ada-code-mappings';
import { ItemCodeToTreatmentToXSLX } from './item-code-to-treatment-to-xlsx';
import { XLSXToItemCodeToTreatment } from './xlsx-to-item-code-to-treatment';
import { PracticeMigration } from '../practice-migrations';

const normalise = (name: string): string =>
  name.trim().toLowerCase().replace(/,/g, '');

export const ITEM_CODE_TO_TREATMENT_CUSTOM_MAPPING_TYPE =
  'itemCodeToTreatmentMapping';

export const ITEM_CODE_TO_TREATMENT_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Item Code To Treatment',
    description: `Used for mapping Item Codes to Treatments in Principle.`,
    type: ITEM_CODE_TO_TREATMENT_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Item Code',
    destinationIdentifier: 'Treatment Name',
    associatedValue: '',
  },
});

export abstract class BaseItemCodeToTreatmentMappingHandler<
  ItemCode extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<ItemCode>,
> extends BaseCustomMappingHandler<ITreatmentConfiguration> {
  customMapping = ITEM_CODE_TO_TREATMENT_MAPPING;
  abstract translateFn: (record: ItemCode['data']) => IBaseMigrationItemCode;
  abstract sourceEntity: SourceEntity;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();

    const options = records
      .map((record) => record.data)
      .map((record) => {
        const translated = this.translateFn(record);
        return {
          label: !translated.description
            ? translated.itemCode
            : `${translated.itemCode} - ${translated.description}`,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ITreatmentConfiguration>[]> {
    return snapshot(
      TreatmentConfiguration.all$(migration.configuration.brand).pipe(
        toINamedDocuments(),
        multiSortBy$(nameSorter())
      )
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, itemCodes, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, itemCodes, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToItemCodeToTreatment()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await resolveWithConcurrencyForAll(records, DELETE_CONCURRENCY, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceRecords = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const codeToSourceInfo = new Map<
      string,
      { sourceIdentifier: string; sourceLabel: string }
    >();
    sourceRecords.forEach((record) => {
      const translated = this.translateFn(record.data);
      codeToSourceInfo.set(translated.itemCode.trim(), {
        sourceIdentifier: this.sourceEntity
          .getSourceRecordId(record.data)
          .toString(),
        sourceLabel: translated.description
          ? `${translated.itemCode} - ${translated.description}`
          : translated.itemCode,
      });
    });

    const treatmentConfigurations = await this.getDestinationOptions(migration);
    const treatmentConfigMap = new Map(
      treatmentConfigurations.map((config) => [normalise(config.name), config])
    );

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.map((item) => {
      const code = item.code?.trim();

      if (!code) {
        return;
      }

      const sourceInfo = codeToSourceInfo.get(code);
      const label = item.description
        ? `${item.code} - ${item.description}`
        : item.code;

      if (!sourceInfo && item.mapTo) {
        failedRecords.push({
          sourceIdentifier: code,
          sourceLabel: label,
          reason: 'Item code not found in source options',
        });
        return;
      }

      if (!sourceInfo || !item.mapTo) {
        return;
      }

      const treatmentConfiguration = treatmentConfigMap.get(
        normalise(item.mapTo)
      );

      if (!treatmentConfiguration) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find treatment configuration for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: sourceInfo.sourceIdentifier,
          sourceLabel: label,
          reason: `Destination identifier "${item.mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        destinationIdentifier: treatmentConfiguration.ref,
        sourceIdentifier: sourceInfo.sourceIdentifier,
        sourceLabel: sourceInfo.sourceLabel,
      };
    });

    const results = await this.upsertBulk(
      compact(recordsForUpload),
      translationMap
    );
    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords: [...failedRecords, ...results.failedRecords],
    });
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const sourceRecords = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();

    const sourceOptions = sourceRecords
      .map((record) => record.data)
      .map((record) => this.translateFn(record));
    const records = await this.getRecords(translationMap);
    const mappedItemCodes = records
      .filter((record) => !!record.destinationIdentifier)
      .map((record) => record.sourceIdentifier);
    const unmappedItemCodes = sourceOptions.filter(
      (option) => !mappedItemCodes.includes(option.itemCode)
    );

    const treatmentConfigurations = await this.getDestinationOptions(migration);
    const suggestedMappings = ITEM_CODE_TO_TREATMENT_DEFAULT_MAPPINGS;

    const recordsForUpload = unmappedItemCodes.map((item) => {
      let suggestedMappingTreatmentName =
        suggestedMappings[item.itemCode.toString()] ?? undefined;
      if (!suggestedMappingTreatmentName) {
        const serviceCode = ServiceProviderHandler.findServiceCode(
          item.itemCode.toString()
        );
        if (serviceCode) {
          const parentCode = PricedServiceCodeTranslator.convertToType(
            serviceCode,
            ServiceCodeType.ADA
          );

          suggestedMappingTreatmentName =
            suggestedMappings[parentCode.code] ?? undefined;
        }
      }

      const mappedTreatment = suggestedMappingTreatmentName
        ? treatmentConfigurations.find(
            (treatment) => treatment.name === suggestedMappingTreatmentName
          )
        : undefined;

      return mappedTreatment
        ? {
            destinationIdentifier: mappedTreatment.ref,
            sourceIdentifier: item.id.toString(),
            sourceLabel: item.itemCode,
          }
        : undefined;
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);
  }

  protected _prepareExporterItemCodes(
    options: IBaseMigrationItemCode[]
  ): IBaseMigrationItemCode[] {
    return sortBy(options, 'itemCode');
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    itemCodes: IBaseMigrationItemCode[];
    translator: ItemCodeToTreatmentToXSLX;
  }> {
    const fileName = this.getFileName();
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();

    const options = records
      .map((record) => record.data)
      .map((record) => this.translateFn(record));

    const itemCodes = this._prepareExporterItemCodes(options);

    const translationMap = new TranslationMapHandler(migration);
    const translator = new ItemCodeToTreatmentToXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap),
      ITEM_CODE_TO_TREATMENT_DEFAULT_MAPPINGS
    );
    return { fileName, itemCodes, translator };
  }
}
